import { useEffect, useMemo, useRef, useState } from 'react';
import { AIClient } from '@tagit/ai-client-core';
import {
  AIClientProvider,
  ChatWidget,
  type ChatWidgetMobilePresentation,
  type ChatWidgetPresentation,
} from '@tagit/ai-client-react';
import '@tagit/ai-client-react/chat-widget.css';
import { readTagItAnonymousIdentity } from '../lib/tagitAiCollector';

const DEFAULT_USE_CASE_ID = 'uc-website-tacky-v2-a6thed';
const DEFAULT_PANEL_WIDTH = '440px';
const DEFAULT_MEMORY_MAX_TURNS = 6;

type TagItAiDemoProps = {
  presentation?: ChatWidgetPresentation;
  mobilePresentation?: ChatWidgetMobilePresentation;
  mobileBreakpointPx?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  panelWidth: string;
  onPanelWidthChange: (width: string) => void;
  resizable?: boolean;
  tabHeightPercent?: number;
  minWidthPx?: number;
  maxWidthPx?: number;
  launchPrompt?: { id: number; text: string } | null;
  onLaunchPromptConsumed?: () => void;
};

export default function TagItAiDemo({
  presentation = 'sidePanel',
  mobilePresentation = 'fullscreen',
  mobileBreakpointPx = 960,
  open,
  onOpenChange,
  panelWidth,
  onPanelWidthChange,
  resizable = true,
  tabHeightPercent = 18,
  minWidthPx = 360,
  maxWidthPx,
  launchPrompt,
  onLaunchPromptConsumed,
}: TagItAiDemoProps) {
  const orchestratorBaseUrl = `${window.location.origin}/api/ai-orchestrator`;
  const useCaseId =
    (import.meta.env.VITE_TAGIT_AI_ORCHESTRATOR_USE_CASE_ID as string | undefined)?.trim() ||
    DEFAULT_USE_CASE_ID;
  const collectorBaseUrl =
    (import.meta.env.VITE_TAGIT_AI_COLLECTOR_BASE_URL as string | undefined)?.trim() || '';
  const collectorTagId =
    (import.meta.env.VITE_TAGIT_AI_COLLECTOR_TAG_ID as string | undefined)?.trim() || '';
  const conversationMemoryMaxTurns = Number(
    (import.meta.env.VITE_TAGIT_AI_CONVERSATION_MEMORY_MAX_TURNS as string | undefined)?.trim() ||
      DEFAULT_MEMORY_MAX_TURNS
  );
  const [assistantIdentity, setAssistantIdentity] = useState(() => readTagItAnonymousIdentity());
  const lastLaunchPromptIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!collectorBaseUrl || !collectorTagId) {
      return;
    }

    let alive = true;
    const timeoutAt = Date.now() + 2500;

    const tick = () => {
      if (!alive) return;
      const nextIdentity = readTagItAnonymousIdentity();
      setAssistantIdentity((current) => {
        const hasChanged =
          current.userId !== nextIdentity.userId ||
          current.entityId !== nextIdentity.entityId ||
          current.entityidType !== nextIdentity.entityidType ||
          current.entityidOwner !== nextIdentity.entityidOwner ||
          current.sessionId !== nextIdentity.sessionId;

        return hasChanged ? nextIdentity : current;
      });

      if (nextIdentity.sessionId || Date.now() >= timeoutAt) {
        return;
      }

      window.setTimeout(tick, 150);
    };

    tick();

    return () => {
      alive = false;
    };
  }, [collectorBaseUrl, collectorTagId]);

  const client = useMemo(() => {
    return new AIClient({
      orchestrator: {
        baseUrl: orchestratorBaseUrl,
        useCaseId,
        timeoutMs: 30000,
        maxRetries: 3,
      },
      user: {
        entityId: assistantIdentity.entityId,
        userId: assistantIdentity.userId,
        entityidType: assistantIdentity.entityidType,
        entityidAlgo: assistantIdentity.entityidAlgo,
        entityidOwner: assistantIdentity.entityidOwner,
      },
      conversation: {
        memoryMaxTurns: Number.isFinite(conversationMemoryMaxTurns)
          ? Math.max(0, Math.trunc(conversationMemoryMaxTurns))
          : DEFAULT_MEMORY_MAX_TURNS,
      },
      conversationHistory: {
        enabled: true,
        recentLimit: 5,
        allowRename: true,
        allowArchive: true,
        surface: 'website-homepage',
        source: 'knotiv-website',
      },
      collector: collectorBaseUrl && collectorTagId
        ? {
            enabled: true,
            baseUrl: collectorBaseUrl,
            tagId: collectorTagId,
            acceptCookies: false,
            agentId: useCaseId,
            eventSubContext: 'website-homepage',
            sessionId: assistantIdentity.sessionId,
          }
        : undefined,
      ui: {
        streaming: true,
        showToolCalls: false,
      },
    });
  }, [
    assistantIdentity.entityId,
    assistantIdentity.entityidAlgo,
    assistantIdentity.entityidOwner,
    assistantIdentity.entityidType,
    assistantIdentity.sessionId,
    assistantIdentity.userId,
    collectorBaseUrl,
    collectorTagId,
    conversationMemoryMaxTurns,
    orchestratorBaseUrl,
    useCaseId,
  ]);

  useEffect(() => {
    if (!client || !open || !launchPrompt) return;
    if (lastLaunchPromptIdRef.current === launchPrompt.id) return;

    lastLaunchPromptIdRef.current = launchPrompt.id;
    void client.sendMessage(launchPrompt.text).finally(() => {
      onLaunchPromptConsumed?.();
    });
  }, [client, launchPrompt, onLaunchPromptConsumed, open]);

  return (
    <AIClientProvider client={client}>
      <div className="tagit-ai-demo-shell">
        <ChatWidget
          presentation={presentation}
          mobilePresentation={mobilePresentation}
          mobileBreakpointPx={mobileBreakpointPx}
          theme="dark"
          side="right"
          open={open}
          onOpenChange={onOpenChange}
          title="Knotiv Guide"
          titleIconUrl="/brand/knotiv/knotiv-ai-profile.png"
          tabLabel="Ask Knotiv"
          tabIconUrl="/brand/knotiv/knotiv-ai-profile.png"
          tabHeightPercent={tabHeightPercent}
          showBackdrop={false}
          closeOnOutsideClick={false}
          resizable={resizable}
          width={panelWidth || DEFAULT_PANEL_WIDTH}
          minWidthPx={minWidthPx}
          maxWidthPx={maxWidthPx}
          onWidthChange={onPanelWidthChange}
          className="tagit-ai-demo-widget"
        />
      </div>
    </AIClientProvider>
  );
}
